import React from 'react';
import {Badge, Button, Modal, ModalBody, ModalHeader} from 'reactstrap';
import API from "@beardeddevops/react.api";
import Framework from "@beardeddevops/react.framework";
import Types from "constants/Types";
import ObjectOverride from "../ObjectOverride";
import MrpLocationControl from "components/Parts/MrpLocationControl";
import {applyMrpLocationToRequest, getMrpLocationSignature, syncMrpActiveLocation} from "utils/mrpLocation";

const LOCATION_NOTE = 'ERP reads and writes follow the active location shown here. Changing it updates data across ERP screens.';

class MrpJobTracker extends ObjectOverride {
	constructor(props) {
		super(props, Types.Mrp_Jobs._name);
		this.state = {
			loading: true,
			loadError: '',
			jobs: [],
			now: Date.now(),
			selectedJobId: '',
			savingJobIds: {},
			showCompleted: true,
			logsModalOpen: false,
			logsLoading: false,
			logsError: '',
			logsEntries: [],
			logsJobId: null,
			logsJobName: '',
			logsFilterType: 'all',
			logsFilterText: '',
			logsFilterFrom: '',
			logsFilterTo: '',
		};
		this.logsRequestToken = 0;
	}

	componentDidMount() {
		syncMrpActiveLocation(this.props.rootState);
		this.loadJobs().then();
	}

	componentDidUpdate(prevProps, prevState) {
		if (getMrpLocationSignature(prevProps?.rootState) !== getMrpLocationSignature(this.props?.rootState)) {
			this.logsRequestToken += 1;
			this.setState({
				selectedJobId: '',
				logsModalOpen: false,
				logsLoading: false,
				logsError: '',
				logsEntries: [],
				logsJobId: null,
				logsJobName: '',
				logsFilterType: 'all',
				logsFilterText: '',
				logsFilterFrom: '',
				logsFilterTo: '',
			}, () => {
				this.loadJobs().then();
			});
		}
		if (prevState?.jobs !== this.state.jobs) {
			this.manageTick();
		}
	}

	componentWillUnmount() {
		this.stopTick();
	}

	getAccountParent = () => {
		return this.props?.rootState?.account_number || '9999-9999999';
	}

	loadJobs = async () => {
		this.setState({loading: true, loadError: ''});
		try {
			let jobModel = new API.BuildClass(Types.Mrp_Jobs._name, 'GET_NORMALIZED');
			jobModel.parent = this.getAccountParent();
			applyMrpLocationToRequest(jobModel, this.props.rootState);
			const result = await jobModel.submit(null, 'GET_NORMALIZED');
			const rawItems = result?.items ?? result?.item ?? result?.data ?? result ?? [];
			let items = Array.isArray(rawItems) ? rawItems : (rawItems ? [rawItems] : []);
			if (items.length === 0 && rawItems && typeof rawItems === 'object') {
				const hasIdentity = rawItems.pkey || rawItems.id;
				items = hasIdentity ? [rawItems] : [];
			}
			const jobs = items.map(this.normalizeJob);
			const sortedJobs = [...jobs].sort((a, b) => {
				const aActive = this.isJobActive(a.status);
				const bActive = this.isJobActive(b.status);
				if (aActive === bActive) {
					return 0;
				}
				return aActive ? -1 : 1;
			});
			const visibleJobs = this.state.showCompleted
				? sortedJobs
				: sortedJobs.filter((job) => this.isJobActive(job.status));
			const selectedJobId = visibleJobs.some((job) => job.id === this.state.selectedJobId)
				? this.state.selectedJobId
				: (visibleJobs[0]?.id || '');
			this.setState({
				jobs: sortedJobs,
				selectedJobId,
				loading: false,
			}, this.manageTick);
		} catch (error) {
			this.setState({loading: false, loadError: 'Failed to load jobs.'}, this.manageTick);
		}
	}

	parseWorkflow = (workflow) => {
		if (!workflow) {
			return null;
		}
		if (typeof workflow === 'string') {
			try {
				return JSON.parse(workflow);
			} catch (error) {
				return null;
			}
		}
		if (typeof workflow === 'object') {
			return workflow;
		}
		return null;
	}

	normalizeJob = (job) => {
		const workflow = this.parseWorkflow(job.workflow);
		const id = job.pkey || job.id;
		const status = job.status || workflow?.job?.status || 'in_progress';
		const name = job.name || workflow?.job?.name || `Job ${id}`;
		const modelName = workflow?.model?.name || workflow?.job?.model_name || '';
		const quantity = workflow?.job?.quantity ?? workflow?.quantity ?? job.quantity ?? job.job_quantity ?? job.qty ?? '';
		const lotId = job.lot || workflow?.job?.lot_id || '';
		return {
			...job,
			id,
			name,
			status,
			workflow,
			modelName,
			quantity,
			lotId,
		};
	}

	isJobActive = (status) => {
		if (!status) {
			return true;
		}
		const normalized = `${status}`.toLowerCase();
		return !['complete', 'completed', 'done', 'closed', 'archived'].includes(normalized);
	}

	formatStatusLabel = (status) => {
		if (!status) {
			return 'In Progress';
		}
		return `${status}`.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
	}

	getStatusColor = (status) => {
		const normalized = `${status || ''}`.toLowerCase();
		if (['complete', 'completed', 'done'].includes(normalized)) {
			return 'success';
		}
		if (['blocked', 'paused', 'on hold', 'hold'].includes(normalized)) {
			return 'warning';
		}
		if (['error', 'failed', 'canceled'].includes(normalized)) {
			return 'danger';
		}
		if (['in progress', 'in_progress', 'active', 'started'].includes(normalized)) {
			return 'primary';
		}
		return 'secondary';
	}

	getStationKey = (station) => station.station_id || station.station_name || '';

	getPartKey = (part) => part.part_id || part.part_name || '';

	getOrderedStations = (stations) => {
		return [...(stations || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
	}

	getStationTotals = (station) => {
		const parts = station.parts || [];
		const required = parts.reduce((sum, part) => sum + (Number(part.required_quantity) || 0), 0);
		const completed = parts.reduce((sum, part) => sum + (Number(part.completed_quantity) || 0), 0);
		return {required, completed};
	}

	isStationComplete = (station, jobQuantity = 1) => {
		const hasParts = (station.parts || []).length > 0;
		if (!hasParts) {
			return this.getNoPartsCompletedUnits(station, jobQuantity) >= Math.max(1, Number(jobQuantity) || 1);
		}
		const normalizedQuantity = Math.max(1, Number(jobQuantity) || 1);
		let hasRequired = false;
		const isComplete = (station.parts || []).every((part) => {
			const requiredPerUnit = Number(part.required_quantity) || 0;
			if (requiredPerUnit <= 0) {
				return true;
			}
			hasRequired = true;
			const requiredTotal = requiredPerUnit * normalizedQuantity;
			const completed = Number(part.completed_quantity) || 0;
			return completed >= requiredTotal;
		});
		return hasRequired && isComplete;
	}

	getNoPartsCompletedUnits = (station, jobQuantity = 1) => {
		const normalizedQuantity = Math.max(1, Number(jobQuantity) || 1);
		const rawUnits = Number(station?.no_parts_completed_units);
		if (Number.isFinite(rawUnits) && rawUnits > 0) {
			return Math.max(0, Math.min(normalizedQuantity, rawUnits));
		}
		if (station?.manual_complete || station?.status === 'completed') {
			return normalizedQuantity;
		}
		return 0;
	}

	findMatchingPart = (parts, targetPart) => {
		if (!parts || !targetPart) {
			return null;
		}
		if (targetPart.part_id) {
			const matchById = parts.find((part) => `${part.part_id}` === `${targetPart.part_id}`);
			if (matchById) {
				return matchById;
			}
		}
		if (targetPart.part_name) {
			return parts.find((part) => part.part_name === targetPart.part_name);
		}
		return null;
	}

	calculateAvailableUnitsFromSource = (sourceStation) => {
		const sourceParts = sourceStation?.parts || [];
		let minUnits = null;
		sourceParts.forEach((part) => {
			const requiredPerUnit = Number(part.required_quantity) || 0;
			if (requiredPerUnit <= 0) {
				return;
			}
			const sourceCompleted = Number(part.completed_quantity) || 0;
			const unitsForPart = Math.floor(sourceCompleted / requiredPerUnit);
			minUnits = minUnits === null ? unitsForPart : Math.min(minUnits, unitsForPart);
		});
		return minUnits === null ? 0 : minUnits;
	}

	getAvailableUnitsForStation = (orderedStations, index, jobQuantity = 1) => {
		const currentStation = orderedStations[index];
		const requiredSum = this.getStationTotals(currentStation).required;
		if (!currentStation || (currentStation.parts || []).length === 0) {
			return {availableUnits: 0, availablePartsTotal: 0, isBlocked: true};
		}
		if (index === 0) {
			return {
				availableUnits: Number.MAX_SAFE_INTEGER,
				availablePartsTotal: Number.MAX_SAFE_INTEGER,
				isBlocked: false,
			};
		}
		let sourceStation = null;
		for (let i = index - 1; i >= 0; i -= 1) {
			const station = orderedStations[i];
			const hasParts = (station.parts || []).length > 0;
			if (!hasParts) {
				if (!this.isStationComplete(station, jobQuantity)) {
					return {availableUnits: 0, availablePartsTotal: 0, isBlocked: true};
				}
				continue;
			}
			sourceStation = station;
			break;
		}
		if (!sourceStation) {
			return {
				availableUnits: Number.MAX_SAFE_INTEGER,
				availablePartsTotal: Number.MAX_SAFE_INTEGER,
				isBlocked: false,
			};
		}
		const availableUnits = this.calculateAvailableUnitsFromSource(sourceStation);
		const consumedUnits = this.getCompletedUnitsForStation(currentStation);
		const remainingUnits = Math.max(0, availableUnits - consumedUnits);
		const availablePartsTotal = requiredSum > 0 ? remainingUnits * requiredSum : 0;
		return {availableUnits: remainingUnits, availablePartsTotal, isBlocked: false};
	}

	getCompletedUnitsForStation = (station) => {
		const parts = station?.parts || [];
		let minUnits = null;
		parts.forEach((part) => {
			const requiredPerUnit = Number(part.required_quantity) || 0;
			if (requiredPerUnit <= 0) {
				return;
			}
			const completed = Number(part.completed_quantity) || 0;
			const unitsForPart = Math.floor(completed / requiredPerUnit);
			minUnits = minUnits === null ? unitsForPart : Math.min(minUnits, unitsForPart);
		});
		return minUnits === null ? 0 : minUnits;
	}

	getCompletionStation = (stations) => {
		const orderedStations = this.getOrderedStations(stations);
		for (let index = orderedStations.length - 1; index >= 0; index -= 1) {
			const station = orderedStations[index];
			if ((station.parts || []).length > 0) {
				return station;
			}
		}
		return orderedStations[orderedStations.length - 1] || null;
	}

	getStationFlowState = (stations, stationKey, jobQuantity = 1) => {
		const orderedStations = this.getOrderedStations(stations);
		for (let index = 0; index < orderedStations.length; index += 1) {
			const station = orderedStations[index];
			const key = this.getStationKey(station);
			const hasParts = (station.parts || []).length > 0;
			if (key === stationKey) {
				const {required, completed} = this.getStationTotals(station);
				const isComplete = this.isStationComplete(station, jobQuantity);
				let availablePartsTotal = required;
				let availableUnits = 0;
				let isLocked = false;
				if (index > 0) {
					if (hasParts) {
						const availability = this.getAvailableUnitsForStation(orderedStations, index, jobQuantity);
						availableUnits = availability.availableUnits;
						availablePartsTotal = availability.availablePartsTotal;
						const hasUnits = availableUnits >= 1;
						isLocked = !hasUnits;
					} else {
						isLocked = !this.isStationComplete(orderedStations[index - 1], jobQuantity);
					}
				}
				return {
					station,
					index,
					hasParts,
					required,
					completed,
					available: availablePartsTotal,
					availableUnits,
					isLocked,
					isComplete,
				};
			}
		}
		return null;
	}

	getActiveTimeEntry = (part) => {
		const entries = part.time_entries || [];
		return entries.find((entry) => !entry.ended_at) || null;
	}

	getPartEntries = (part, station) => {
		const stationEntries = station?.time_entries || [];
		const partStationId = part?.job_station_part_id ?? null;
		if (partStationId !== null && partStationId !== undefined) {
			const scopedEntries = stationEntries.filter((entry) => (
				`${entry?.job_station_part_id ?? ''}` === `${partStationId}`
			));
			if (scopedEntries.length > 0) {
				return scopedEntries;
			}
		}
		return part?.time_entries || [];
	}

	getPartEntriesByType = (part, station, type) => {
		const entries = this.getPartEntries(part, station);
		const normalizedType = `${type || ''}`.toLowerCase();
		return entries.filter((entry) => {
			const entryType = `${entry?.entry_type || ''}`.toLowerCase();
			if (!entryType && normalizedType === 'labor') {
				return true;
			}
			return entryType === normalizedType;
		});
	}

	isEntryArchived = (entry) => {
		if (!entry) {
			return false;
		}
		const value = entry.archived;
		return value === true || value === 1 || value === '1';
	}

	getStationEntriesByType = (station, type) => {
		const entries = station?.time_entries || [];
		const normalizedType = `${type || ''}`.toLowerCase();
		return entries.filter((entry) => {
			if (this.isEntryArchived(entry)) {
				return false;
			}
			const entryType = `${entry?.entry_type || ''}`.toLowerCase();
			if (!entryType && normalizedType === 'labor') {
				return true;
			}
			return entryType === normalizedType;
		});
	}

	getStationActiveEntryByType = (station, type) => {
		return this.getStationEntriesByType(station, type).find((entry) => !entry.ended_at) || null;
	}

	getStationTotalSecondsByType = (station, type) => {
		return this.getStationEntriesByType(station, type)
			.reduce((sum, entry) => sum + this.getEntrySeconds(entry), 0);
	}

	getStationTotalSeconds = (station) => {
		const entries = station?.time_entries || [];
		return entries
			.filter((entry) => !this.isEntryArchived(entry))
			.reduce((sum, entry) => sum + this.getEntrySeconds(entry), 0);
	}

	getEntryType = (entry) => {
		const normalizedType = `${entry?.entry_type || ''}`.toLowerCase();
		if (!normalizedType) {
			return 'labor';
		}
		return normalizedType;
	}

	collectJobTimeEntries = (stations) => {
		const dedupedEntries = new Map();
		const addEntry = (entry, fallbackKey) => {
			if (!entry || this.isEntryArchived(entry)) {
				return;
			}
			const hasEntryId = entry.time_entry_id !== null
				&& entry.time_entry_id !== undefined
				&& `${entry.time_entry_id}` !== '';
			const entryKey = hasEntryId ? `id:${entry.time_entry_id}` : `fallback:${fallbackKey}`;
			if (hasEntryId && dedupedEntries.has(entryKey)) {
				return;
			}
			dedupedEntries.set(entryKey, entry);
		};

		(stations || []).forEach((station, stationIndex) => {
			(station?.time_entries || []).forEach((entry, entryIndex) => {
				addEntry(entry, `station:${stationIndex}:${entryIndex}`);
			});
			(station?.parts || []).forEach((part, partIndex) => {
				(part?.time_entries || []).forEach((entry, entryIndex) => {
					addEntry(entry, `part:${stationIndex}:${partIndex}:${entryIndex}`);
				});
			});
		});

		return Array.from(dedupedEntries.values());
	}

	getJobTimeTotals = (stations) => {
		const entries = this.collectJobTimeEntries(stations);
		let laborSeconds = 0;
		let machineSeconds = 0;
		let laborActive = false;
		let machineActive = false;
		let activeEntryCount = 0;

		entries.forEach((entry) => {
			const entryType = this.getEntryType(entry);
			const seconds = this.getEntrySeconds(entry);
			const isActive = !entry.ended_at;
			if (isActive) {
				activeEntryCount += 1;
			}
			if (entryType === 'machine') {
				machineSeconds += seconds;
				if (isActive) {
					machineActive = true;
				}
				return;
			}
			laborSeconds += seconds;
			if (isActive) {
				laborActive = true;
			}
		});

		return {
			laborSeconds,
			machineSeconds,
			totalSeconds: laborSeconds + machineSeconds,
			laborActive,
			machineActive,
			activeEntryCount,
		};
	}

	getStationQuantitySummary = (station, jobQuantity = 1) => {
		const normalizedJobQuantity = Math.max(1, Number(jobQuantity) || 1);
		const parts = station?.parts || [];
		const hasParts = parts.length > 0;
		if (!hasParts) {
			const requiredTotal = normalizedJobQuantity;
			const completedTotal = this.getNoPartsCompletedUnits(station, normalizedJobQuantity);
			return {
				hasParts,
				requiredTotal,
				completedTotal,
				completedCapped: Math.min(requiredTotal, completedTotal),
			};
		}
		let requiredTotal = 0;
		let completedTotal = 0;
		parts.forEach((part) => {
			const requiredPerUnit = Math.max(0, Number(part.required_quantity) || 0);
			requiredTotal += requiredPerUnit * normalizedJobQuantity;
			completedTotal += Math.max(0, Number(part.completed_quantity) || 0);
		});
		return {
			hasParts,
			requiredTotal,
			completedTotal,
			completedCapped: requiredTotal > 0 ? Math.min(requiredTotal, completedTotal) : 0,
		};
	}

	getWorkflowSummary = (stations, jobQuantity = 1) => {
		const normalizedStations = stations || [];
		let completedStations = 0;
		let inProgressStations = 0;
		let requiredQuantity = 0;
		let completedQuantity = 0;

		normalizedStations.forEach((station) => {
			const quantitySummary = this.getStationQuantitySummary(station, jobQuantity);
			requiredQuantity += quantitySummary.requiredTotal;
			completedQuantity += quantitySummary.completedCapped;

			const complete = this.isStationComplete(station, jobQuantity);
			if (complete) {
				completedStations += 1;
				return;
			}

			const hasActiveTime = (station?.time_entries || []).some((entry) => !this.isEntryArchived(entry) && !entry?.ended_at);
			const normalizedStatus = `${station?.status || ''}`.toLowerCase();
			const hasProgress = quantitySummary.completedTotal > 0
				|| hasActiveTime
				|| ['in_progress', 'in progress', 'active', 'started'].includes(normalizedStatus);

			if (hasProgress) {
				inProgressStations += 1;
			}
		});

		const totalStations = normalizedStations.length;
		const pendingStations = Math.max(0, totalStations - completedStations - inProgressStations);
		const workflowPercent = totalStations > 0
			? Math.min(100, Math.round((completedStations / totalStations) * 100))
			: 0;
		const quantityPercent = requiredQuantity > 0
			? Math.min(100, Math.round((completedQuantity / requiredQuantity) * 100))
			: 0;
		const completionStation = this.getCompletionStation(normalizedStations);
		let unitsCompleted = 0;
		if (completionStation) {
			if ((completionStation.parts || []).length > 0) {
				unitsCompleted = this.getCompletedUnitsForStation(completionStation);
			} else {
				unitsCompleted = this.getNoPartsCompletedUnits(completionStation, jobQuantity);
			}
		}
		const normalizedJobQuantity = Math.max(1, Number(jobQuantity) || 1);
		const unitsCompletedCapped = Math.max(0, Math.min(normalizedJobQuantity, unitsCompleted));
		const unitsPercent = normalizedJobQuantity > 0
			? Math.min(100, Math.round((unitsCompletedCapped / normalizedJobQuantity) * 100))
			: 0;

		return {
			totalStations,
			completedStations,
			inProgressStations,
			pendingStations,
			requiredQuantity,
			completedQuantity,
			workflowPercent,
			quantityPercent,
			unitsCompleted: unitsCompletedCapped,
			unitsPercent,
		};
	}

	getEntrySeconds = (entry) => {
		if (!entry) {
			return 0;
		}
		if (entry.started_at && !entry.ended_at) {
			const started = Date.parse(entry.started_at);
			const now = this.state.now || Date.now();
			if (!Number.isNaN(started)) {
				return Math.max(0, Math.round((now - started) / 1000));
			}
		}
		if (entry.duration_minutes || entry.duration_minutes === 0) {
			return Math.max(0, Math.round((Number(entry.duration_minutes) || 0) * 60));
		}
		if (entry.minutes || entry.minutes === 0) {
			return Math.max(0, Math.round((Number(entry.minutes) || 0) * 60));
		}
		if (entry.started_at && entry.ended_at) {
			const started = Date.parse(entry.started_at);
			const ended = Date.parse(entry.ended_at);
			if (!Number.isNaN(started) && !Number.isNaN(ended)) {
				return Math.max(0, Math.round((ended - started) / 1000));
			}
		}
		return 0;
	}

	getPartActiveEntryByType = (part, station, type) => {
		return this.getPartEntriesByType(part, station, type).find((entry) => !entry.ended_at) || null;
	}

	getPartTotalSecondsByType = (part, station, type) => {
		return this.getPartEntriesByType(part, station, type)
			.reduce((sum, entry) => sum + this.getEntrySeconds(entry), 0);
	}

	getPartTotalSeconds = (part, station) => {
		const entries = this.getPartEntries(part, station);
		return entries.reduce((sum, entry) => sum + this.getEntrySeconds(entry), 0);
	}

	formatElapsed = (totalSeconds) => {
		const seconds = Math.max(0, Math.round(totalSeconds || 0));
		const hours = Math.floor(seconds / 3600);
		const minutes = Math.floor((seconds % 3600) / 60);
		const remainderSeconds = seconds % 60;
		const pad = (value) => String(value).padStart(2, '0');
		if (hours > 0) {
			return `${hours}:${pad(minutes)}:${pad(remainderSeconds)}`;
		}
		return `${minutes}:${pad(remainderSeconds)}`;
	}

	hasActiveEntries = (stations) => {
		return this.collectJobTimeEntries(stations).some((entry) => !entry?.ended_at);
	}

	hasAnyActiveTimers = () => {
		return this.state.jobs.some((job) => this.hasActiveEntries(job?.workflow?.stations || []));
	}

	manageTick = () => {
		if (this.hasAnyActiveTimers()) {
			this.startTick();
			return;
		}
		this.stopTick();
	}

	startTick = () => {
		if (this._tickInterval) {
			return;
		}
		this._tickInterval = setInterval(() => {
			this.setState({now: Date.now()});
		}, 1000);
	}

	stopTick = () => {
		if (this._tickInterval) {
			clearInterval(this._tickInterval);
			this._tickInterval = null;
		}
	}

	renderJobListTimerMetric = (label, value, isActive, tone = 'secondary') => {
		const toneMap = {
			primary: {
				background: '#e8f2ff',
				text: '#1e4f91',
			},
			info: {
				background: '#e8f8fb',
				text: '#167a90',
			},
			secondary: {
				background: '#f3f5f7',
				text: '#4a5562',
			},
		};
		const palette = toneMap[tone] || toneMap.secondary;
		return (
			<div key={label} style={{...trackerStyles.jobListTimerChip, backgroundColor: palette.background, color: palette.text}}>
				<span style={trackerStyles.jobListTimerLabel}>{label}</span>
				<span style={trackerStyles.jobListTimerValue}>{value}</span>
				{isActive ? (
					<span style={trackerStyles.jobListTimerRunning}>Live</span>
				) : null}
			</div>
		);
	}

	formatLogTimestamp = (value) => {
		if (!value) {
			return '-';
		}
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) {
			return value;
		}
		return date.toLocaleString();
	}

	normalizeLogTerminology = (text) => {
		const value = `${text || ''}`;
		return value
			.replace(/\b[Pp]arts\b/g, (match) => (match[0] === 'P' ? 'Items' : 'items'))
			.replace(/\b[Pp]art\b/g, (match) => (match[0] === 'P' ? 'Item' : 'item'));
	}

	formatLogDescription = (entry) => {
		const eventType = `${entry?.event_type || ''}`.toLowerCase();
		const metadata = (entry && typeof entry === 'object' && entry.metadata && typeof entry.metadata === 'object')
			? entry.metadata
			: {};
		if (eventType === 'quantity') {
			const itemName = `${metadata?.item_name || metadata?.part_name || ''}`.trim();
			const delta = Number(metadata?.delta);
			const from = Number(metadata?.from);
			const to = Number(metadata?.to);
			const hasDelta = Number.isFinite(delta);
			const hasFromTo = Number.isFinite(from) && Number.isFinite(to);
			const itemLabel = itemName ? `Item ${itemName}` : 'Item';
			if (hasDelta && hasFromTo) {
				const direction = delta >= 0 ? 'increased' : 'decreased';
				return `${itemLabel} quantity ${direction} by ${Math.abs(delta)} (${from} -> ${to}).`;
			}
		}
		if (entry?.description) {
			return this.normalizeLogTerminology(entry.description);
		}
		const action = entry?.action ? `${entry.action}`.replace(/_/g, ' ') : 'updated';
		const eventLabel = entry?.event_type ? `${entry.event_type}`.replace(/_/g, ' ') : 'event';
		return this.normalizeLogTerminology(`${eventLabel} ${action}`.replace(/\b\w/g, (char) => char.toUpperCase()));
	}

	getLogActorName = (entry) => {
		const direct = `${entry?.actor_name || ''}`.trim();
		if (direct) {
			return direct;
		}
		const employee = `${entry?.employee_name || ''}`.trim();
		if (employee) {
			return employee;
		}
		const metaActor = `${entry?.metadata?.actor_name || ''}`.trim();
		if (metaActor) {
			return metaActor;
		}
		const metaEmployee = `${entry?.metadata?.employee_name || ''}`.trim();
		if (metaEmployee) {
			return metaEmployee;
		}
		return '';
	}

	getFilteredLogs = () => {
		const {logsEntries, logsFilterType, logsFilterText, logsFilterFrom, logsFilterTo, logsJobId} = this.state;
		const search = `${logsFilterText || ''}`.trim().toLowerCase();
		return logsEntries.filter((entry) => {
			if (logsJobId && `${entry?.job ?? ''}` !== `${logsJobId}`) {
				return false;
			}
			const entryType = `${entry?.event_type || ''}`.toLowerCase();
			if (logsFilterType !== 'all' && entryType !== logsFilterType) {
				return false;
			}
			const entryTime = entry?.created ? new Date(entry.created) : null;
			if (logsFilterFrom && entryTime && !Number.isNaN(entryTime.getTime())) {
				const fromDate = new Date(`${logsFilterFrom}T00:00:00`);
				if (entryTime < fromDate) {
					return false;
				}
			}
			if (logsFilterTo && entryTime && !Number.isNaN(entryTime.getTime())) {
				const toDate = new Date(`${logsFilterTo}T23:59:59.999`);
				if (entryTime > toDate) {
					return false;
				}
			}
			if (!search) {
				return true;
			}
			const description = `${this.formatLogDescription(entry)}`.toLowerCase();
			const action = `${entry?.action || ''}`.toLowerCase();
			const type = `${entry?.event_type || ''}`.toLowerCase();
			const creator = `${entry?.creator || ''}`.toLowerCase();
			const actor = `${this.getLogActorName(entry)}`.toLowerCase();
			return description.includes(search)
				|| action.includes(search)
				|| type.includes(search)
				|| creator.includes(search)
				|| actor.includes(search);
		});
	}

	getLogTypeOptions = () => {
		const {logsEntries} = this.state;
		const unique = Array.from(new Set(logsEntries.map((entry) => `${entry?.event_type || ''}`.toLowerCase()).filter(Boolean)));
		return unique.sort();
	}

	escapeCsvField = (value) => {
		const text = `${value ?? ''}`;
		if (text.includes('"') || text.includes(',') || text.includes('\n')) {
			return `"${text.replace(/"/g, '""')}"`;
		}
		return text;
	}

	handleExportLogsCsv = () => {
		const filteredLogs = this.getFilteredLogs();
		if (!filteredLogs.length) {
			return;
		}
		const headers = ['time', 'actor', 'event', 'type', 'action', 'creator', 'job', 'station_id', 'item_id', 'time_entry_id'];
		const rows = filteredLogs.map((entry) => ([
			this.formatLogTimestamp(entry.created),
			this.getLogActorName(entry) || '',
			this.formatLogDescription(entry),
			this.formatStatusLabel(entry.event_type || 'event'),
			entry.action || '',
			entry.creator || '',
			entry.job || '',
			entry.station_id || '',
			entry.part_id || '',
			entry.time_entry_id || '',
		]));
		const csvContent = [headers, ...rows]
			.map((row) => row.map(this.escapeCsvField).join(','))
			.join('\n');
		const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
		const url = URL.createObjectURL(blob);
		const dateStamp = new Date().toISOString().slice(0, 10);
		const fileNameBase = (this.state.logsJobName || 'job-logs').toLowerCase().replace(/[^a-z0-9]+/g, '-');
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.download = `${fileNameBase}-${dateStamp}.csv`;
		document.body.appendChild(anchor);
		anchor.click();
		document.body.removeChild(anchor);
		URL.revokeObjectURL(url);
	}

	addQueryableProperties = (model, keys) => {
		const queryable = {queryable: true, settable: true};
		keys.forEach((key) => {
			model.properties[key] = queryable;
		});
	}

	handleOpenLogs = async (job) => {
		const workflow = this.parseWorkflow(job?.workflow);
		const jobId = job?.id || job?.pkey || null;
		const jobName = job?.name || workflow?.job?.name || `Job ${jobId || ''}`.trim();
		const requestToken = this.logsRequestToken + 1;
		this.logsRequestToken = requestToken;
		this.setState({
			logsModalOpen: true,
			logsLoading: true,
			logsError: '',
			logsEntries: [],
			logsJobId: jobId,
			logsJobName: jobName,
			logsFilterType: 'all',
			logsFilterText: '',
			logsFilterFrom: '',
			logsFilterTo: '',
		});
		try {
			const model = new API.BuildClass(Types.Mrp_Jobs._name, 'GET_LOGS');
			this.addQueryableProperties(model, ['job', 'pkey']);
			model.parent = this.getAccountParent();
			model.object.job = jobId;
			model.object.pkey = jobId;
			applyMrpLocationToRequest(model, this.props.rootState);
			const response = await model.submit(null, 'GET_LOGS');
			if (requestToken !== this.logsRequestToken) {
				return;
			}
			const rawItems = response?.items ?? response?.data ?? response ?? [];
			const logsEntries = Array.isArray(rawItems)
				? rawItems.filter((entry) => `${entry?.job ?? ''}` === `${jobId}`)
				: [];
			this.setState({
				logsEntries,
				logsLoading: false,
			});
		} catch (error) {
			if (requestToken !== this.logsRequestToken) {
				return;
			}
			this.setState({
				logsLoading: false,
				logsError: 'Failed to load job logs.',
			});
		}
	}

	handleCloseLogs = () => {
		this.logsRequestToken += 1;
		this.setState({
			logsModalOpen: false,
			logsLoading: false,
			logsError: '',
			logsEntries: [],
			logsJobId: null,
			logsJobName: '',
			logsFilterType: 'all',
			logsFilterText: '',
			logsFilterFrom: '',
			logsFilterTo: '',
		});
	}

	renderTimeMetricRow = (label, value, isActive = false, tone = 'secondary') => {
		const toneMap = {
			primary: {
				background: '#e8f2ff',
				text: '#1e4f91',
			},
			info: {
				background: '#e8f8fb',
				text: '#167a90',
			},
			secondary: {
				background: '#f3f5f7',
				text: '#4a5562',
			},
		};
		const palette = toneMap[tone] || toneMap.secondary;
		return (
			<div
				className="d-flex align-items-center justify-content-between px-2 py-1 rounded mb-1"
				style={{backgroundColor: palette.background}}
			>
				<span className="small text-uppercase font-weight-bold" style={{letterSpacing: '0.03em', color: palette.text}}>
					{label}
				</span>
				<div className="d-flex align-items-center">
					<span style={{fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: '#1f2933'}}>
						{value}
					</span>
					{isActive ? (
						<Badge color="info" pill className="ml-2">Running</Badge>
					) : null}
				</div>
			</div>
		);
	}

	renderStationTimeSummary = (station) => {
		const laborSeconds = this.getStationTotalSecondsByType(station, 'labor');
		const machineSeconds = this.getStationTotalSecondsByType(station, 'machine');
		const totalSeconds = laborSeconds + machineSeconds;
		const laborActive = !!this.getStationActiveEntryByType(station, 'labor');
		const machineActive = !!this.getStationActiveEntryByType(station, 'machine');
		const showSummary = totalSeconds > 0 || laborActive || machineActive;
		if (!showSummary) {
			return null;
		}
		return (
			<div className="mt-3 p-2 rounded" style={trackerStyles.stationTimeSummaryCard}>
				<div style={trackerStyles.stationTimeSummaryTitle}>Station Timers</div>
				{this.renderTimeMetricRow('Labor', this.formatElapsed(laborSeconds), laborActive, 'primary')}
				{this.renderTimeMetricRow('Machine', this.formatElapsed(machineSeconds), machineActive, 'info')}
				<div className="d-flex align-items-center justify-content-between px-2 pt-1">
					<span className="small text-muted text-uppercase font-weight-bold" style={{letterSpacing: '0.03em'}}>
						Total
					</span>
					<span style={{fontWeight: 700, fontVariantNumeric: 'tabular-nums'}}>
						{this.formatElapsed(totalSeconds)}
					</span>
				</div>
			</div>
		);
	}

	renderQuantityBlock = (title, reqValue, doneValue) => {
		const required = Number(reqValue) || 0;
		const done = Number(doneValue) || 0;
		let state = 'not_started';
		if (required > 0 && done >= required) {
			state = 'complete';
		} else if (done > 0) {
			state = 'in_progress';
		}
		const stateTheme = {
			not_started: {
				bg: '#fff1f2',
				border: '#fecdd3',
				text: '#9f1239',
				label: 'Not Started',
			},
			in_progress: {
				bg: '#fff9db',
				border: '#fde68a',
				text: '#92400e',
				label: 'In Progress',
			},
			complete: {
				bg: '#ecfdf3',
				border: '#bbf7d0',
				text: '#166534',
				label: 'Complete',
			},
		};
		const theme = stateTheme[state];
		return (
			<div className="p-2 rounded mb-2" style={{backgroundColor: theme.bg, border: `1px solid ${theme.border}`}}>
				<div className="d-flex align-items-center justify-content-between mb-1">
					<div className="small text-uppercase font-weight-bold text-muted" style={{letterSpacing: '0.03em'}}>
						{title}
					</div>
					<span
						className="small font-weight-bold"
						style={{color: theme.text}}
					>
						{theme.label}
					</span>
				</div>
				<div className="d-flex align-items-center justify-content-between">
					<div className="mr-2">
						<div className="small text-muted">Done</div>
						<div style={{fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: theme.text}}>{doneValue}</div>
					</div>
					<div>
						<div className="small text-muted">Req</div>
						<div style={{fontWeight: 700, fontVariantNumeric: 'tabular-nums'}}>{reqValue}</div>
					</div>
				</div>
			</div>
		);
	}

	renderSummaryProgress = (label, currentValue, totalValue, percentValue, detail, barClassName = 'bg-primary') => {
		const percent = Math.max(0, Math.min(100, Number(percentValue) || 0));
		return (
			<div className="mb-3 p-2 rounded" style={trackerStyles.summaryProgressBlock}>
				<div className="d-flex justify-content-between align-items-center mb-1">
					<div className="small text-muted font-weight-bold text-uppercase" style={{letterSpacing: '0.03em'}}>
						{label}
					</div>
					<div className="small font-weight-bold" style={{fontVariantNumeric: 'tabular-nums'}}>
						{currentValue} / {totalValue}
					</div>
				</div>
				<div className="progress" style={{height: '6px'}}>
					<div
						className={`progress-bar ${barClassName}`}
						role="progressbar"
						style={{width: `${percent}%`}}
						aria-valuenow={percent}
						aria-valuemin="0"
						aria-valuemax="100"
					/>
				</div>
				{detail ? (
					<div className="small text-muted mt-1">{detail}</div>
				) : null}
			</div>
		);
	}

	getPartCompletionDelta = (part, jobQuantity, mode) => {
		const requiredPerUnit = Number(part?.required_quantity) || 0;
		if (requiredPerUnit <= 0) {
			return 0;
		}
		const normalizedJobQuantity = Math.max(1, Number(jobQuantity) || 1);
		const requiredMax = requiredPerUnit * normalizedJobQuantity;
		const currentRaw = Number(part?.completed_quantity) || 0;
		const current = Math.min(currentRaw, requiredMax);
		if (current >= requiredMax) {
			return 0;
		}
		if (mode === 'all') {
			return Math.max(0, requiredMax - current);
		}
		const remainder = current % requiredPerUnit;
		const target = remainder === 0 ? current + requiredPerUnit : current + (requiredPerUnit - remainder);
		return Math.max(0, Math.min(target, requiredMax) - current);
	}

	setJobSaving = (jobId, isSaving) => {
		this.setState((prevState) => {
			const savingJobIds = {...prevState.savingJobIds};
			if (isSaving) {
				savingJobIds[jobId] = true;
			} else {
				delete savingJobIds[jobId];
			}
			return {savingJobIds};
		});
	}

	recalculateWorkflowStatus = (workflow, now, fallbackQuantity = null) => {
		const stations = workflow.stations || [];
		const jobQuantity = Math.max(
			1,
			Number(workflow.job?.quantity ?? workflow.quantity ?? fallbackQuantity) || 1
		);
		let allStationsComplete = stations.length > 0;
		stations.forEach((station) => {
			const parts = station.parts || [];
			const hasParts = parts.length > 0;
			const noPartsCompletedUnits = this.getNoPartsCompletedUnits(station, jobQuantity);
			let requiredTotal = 0;
			let completedTotal = 0;
			let hasActive = false;
			parts.forEach((part) => {
				const requiredPerUnit = Number(part.required_quantity) || 0;
				const required = requiredPerUnit * jobQuantity;
				const completed = Number(part.completed_quantity) || 0;
				requiredTotal += requiredPerUnit;
				completedTotal += completed;
				if (this.getActiveTimeEntry(part)) {
					hasActive = true;
				}
				if (required > 0 && completed >= required) {
					if (!part.completed_at) {
						part.completed_at = now;
					}
				} else if (part.completed_at && completed < required) {
					part.completed_at = null;
				}
			});
			const wasCompleted = station.status === 'completed';
			const isComplete = hasParts
				? this.isStationComplete(station, jobQuantity)
				: noPartsCompletedUnits >= jobQuantity;
			if (isComplete) {
				station.status = 'completed';
				if (!hasParts) {
					station.manual_complete = true;
					station.no_parts_completed_units = jobQuantity;
				}
				if (!station.completed_at || !wasCompleted) {
					station.completed_at = now;
				}
				if (!station.started_at) {
					station.started_at = now;
				}
			} else if (hasParts && (completedTotal > 0 || hasActive)) {
				station.status = 'in_progress';
				if (!station.started_at) {
					station.started_at = now;
				}
				station.completed_at = null;
			} else if (!hasParts && noPartsCompletedUnits > 0) {
				station.status = 'in_progress';
				station.manual_complete = false;
				station.no_parts_completed_units = noPartsCompletedUnits;
				station.completed_at = null;
				if (!station.started_at) {
					station.started_at = now;
				}
			} else {
				station.status = 'pending';
				station.completed_at = null;
				if (!hasParts) {
					station.manual_complete = false;
					station.no_parts_completed_units = 0;
					station.started_at = null;
				}
			}
			station.totals = {
				required_parts: parts.length,
				required_quantity: requiredTotal,
				completed_quantity: completedTotal,
			};
			if (!isComplete) {
				allStationsComplete = false;
			}
		});
		workflow.job = workflow.job || {};
		workflow.job.status = allStationsComplete ? 'completed' : 'in_progress';
		if (allStationsComplete) {
			if (!workflow.job.completed_at) {
				workflow.job.completed_at = now;
			}
		} else {
			workflow.job.completed_at = null;
		}
		return workflow;
	}

	saveWorkflow = async (jobId, workflow) => {
		this.setJobSaving(jobId, true);
		try {
			let jobModel = new API.BuildClass(Types.Mrp_Jobs._name, 'POST_WORKFLOW');
			jobModel.object.pkey = jobId;
			jobModel.object.id = jobId;
			jobModel.object.workflow = JSON.stringify(workflow);
			jobModel.parent = this.props.rootState.account_number;
			jobModel.object.status = workflow.job?.status || 'in_progress';
			applyMrpLocationToRequest(jobModel, this.props.rootState);
			await jobModel.submit(null, 'POST_WORKFLOW');
			this.setState((prevState) => ({
				jobs: prevState.jobs.map((job) => (
					job.id === jobId ? {...job, workflow, status: jobModel.object.status} : job
				))
			}));
		} catch (error) {
			API.Toast.error('Failed to update job progress');
		} finally {
			this.setJobSaving(jobId, false);
		}
	}

	updateWorkflow = async (jobId, updateFn) => {
		const job = this.state.jobs.find((entry) => entry.id === jobId);
		if (!job || !job.workflow) {
			return;
		}
		const now = new Date().toISOString();
		const workflow = JSON.parse(JSON.stringify(job.workflow));
		const fallbackQuantity = workflow?.job?.quantity
			?? workflow?.quantity
			?? job.quantity
			?? job.job_quantity
			?? job.qty
			?? null;
		if (
			fallbackQuantity !== null
			&& fallbackQuantity !== undefined
			&& `${fallbackQuantity}` !== ''
		) {
			workflow.job = {
				...(workflow.job || {}),
				quantity: fallbackQuantity
			};
		}
		updateFn(workflow, now);
		const updatedWorkflow = this.recalculateWorkflowStatus(workflow, now, fallbackQuantity);
		await this.saveWorkflow(jobId, updatedWorkflow);
	}

	handlePartStart = (jobId, stationKey, partKey) => {
		this.updateWorkflow(jobId, (workflow, now) => {
			const station = (workflow.stations || []).find((entry) => this.getStationKey(entry) === stationKey);
			if (!station) {
				return;
			}
			const part = (station.parts || []).find((entry) => this.getPartKey(entry) === partKey);
			if (!part) {
				return;
			}
			part.time_entries = part.time_entries || [];
			if (this.getActiveTimeEntry(part)) {
				return;
			}
			part.time_entries.push({
				started_at: now,
				ended_at: null,
				duration_minutes: 0,
			});
		});
	}

	handlePartStop = (jobId, stationKey, partKey) => {
		this.updateWorkflow(jobId, (workflow, now) => {
			const station = (workflow.stations || []).find((entry) => this.getStationKey(entry) === stationKey);
			if (!station) {
				return;
			}
			const part = (station.parts || []).find((entry) => this.getPartKey(entry) === partKey);
			if (!part) {
				return;
			}
			const activeEntry = this.getActiveTimeEntry(part);
			if (!activeEntry) {
				return;
			}
			activeEntry.ended_at = now;
			const started = Date.parse(activeEntry.started_at || '');
			const ended = Date.parse(activeEntry.ended_at || '');
			if (!Number.isNaN(started) && !Number.isNaN(ended)) {
				activeEntry.duration_minutes = Math.max(0, Math.round((ended - started) / 60000));
			}
		}).then();
	}

	handlePartCompletion = (jobId, stationKey, partKey, delta) => {
		this.updateWorkflow(jobId, (workflow, now) => {
			const jobQuantity = Math.max(1, Number(workflow.job?.quantity ?? workflow.quantity) || 1);
			const flowState = this.getStationFlowState(workflow.stations || [], stationKey, jobQuantity);
			if (!flowState || !flowState.hasParts) {
				return;
			}
			const station = flowState.station;
			const part = (station.parts || []).find((entry) => this.getPartKey(entry) === partKey);
			if (!part) {
				return;
			}
			const requiredPerUnit = Number(part.required_quantity) || 0;
			const requiredTotal = requiredPerUnit * jobQuantity;
			const current = Number(part.completed_quantity) || 0;
			const next = Math.max(0, current + delta);
			part.completed_quantity = next;
			if (requiredTotal > 0 && next >= requiredTotal && !part.completed_at) {
				part.completed_at = now;
			}
			if (next < requiredTotal) {
				part.completed_at = null;
			}
		});
	}

	handleSelectJob = (jobId) => {
		this.setState({selectedJobId: jobId});
	}

	handleToggleShowCompleted = () => {
		this.setState((prevState) => {
			const showCompleted = !prevState.showCompleted;
			let selectedJobId = prevState.selectedJobId;
			if (!showCompleted) {
				const activeJobs = prevState.jobs.filter((job) => this.isJobActive(job.status));
				if (!activeJobs.some((job) => job.id === selectedJobId)) {
					selectedJobId = activeJobs[0]?.id || '';
				}
			}
			return {showCompleted, selectedJobId};
		});
	}

	renderLogsModal() {
		const {logsModalOpen, logsLoading, logsError, logsEntries, logsJobName, logsFilterType, logsFilterText, logsFilterFrom, logsFilterTo} = this.state;
		const filteredLogs = this.getFilteredLogs();
		const logTypeOptions = this.getLogTypeOptions();
		return (
			<Modal
				isOpen={logsModalOpen}
				toggle={this.handleCloseLogs}
				size="xl"
				style={{maxWidth: '86vw', width: '86vw'}}
			>
				<ModalHeader toggle={this.handleCloseLogs}>
					Job Logs{logsJobName ? `: ${logsJobName}` : ''}
				</ModalHeader>
				<ModalBody>
					<div className="small text-muted mb-3">
						Logs include workflow changes, timer actions, manual time, scrap activity, and time-entry edits for this job. Filters and CSV export only use the rows loaded in this window.
					</div>
					{logsLoading ? (
						<div className="text-center py-3">
							<Framework.Elements.LoadingSpinner isLoading={true}/>
						</div>
					) : null}
					{!logsLoading && logsError ? (
						<div className="alert alert-danger mb-0">{logsError}</div>
					) : null}
					{!logsLoading && !logsError && logsEntries.length === 0 ? (
						<div className="text-muted">No logs found for this job yet.</div>
					) : null}
					{!logsLoading && !logsError && logsEntries.length > 0 ? (
						<div>
							<div className="d-flex align-items-end mb-3" style={{gap: '8px', flexWrap: 'wrap'}}>
								<div>
									<label className="small text-muted mb-1 d-block">Type</label>
									<select
										className="form-control form-control-sm"
										value={logsFilterType}
										onChange={(event) => this.setState({logsFilterType: event.target.value})}
									>
										<option value="all">All Types</option>
										{logTypeOptions.map((type) => (
											<option key={type} value={type}>{this.formatStatusLabel(type)}</option>
										))}
									</select>
								</div>
								<div style={{minWidth: '220px', flex: '1 1 220px'}}>
									<label className="small text-muted mb-1 d-block">Search</label>
									<input
										type="text"
										className="form-control form-control-sm"
										value={logsFilterText}
										onChange={(event) => this.setState({logsFilterText: event.target.value})}
										placeholder="Search event text..."
									/>
								</div>
								<div>
									<label className="small text-muted mb-1 d-block">From</label>
									<input
										type="date"
										className="form-control form-control-sm"
										value={logsFilterFrom}
										onChange={(event) => this.setState({logsFilterFrom: event.target.value})}
									/>
								</div>
								<div>
									<label className="small text-muted mb-1 d-block">To</label>
									<input
										type="date"
										className="form-control form-control-sm"
										value={logsFilterTo}
										onChange={(event) => this.setState({logsFilterTo: event.target.value})}
									/>
								</div>
								<div>
									<Button
										size="sm"
										outline
										color="secondary"
										onClick={() => this.setState({logsFilterType: 'all', logsFilterText: '', logsFilterFrom: '', logsFilterTo: ''})}
									>
										Clear
									</Button>
								</div>
								<div>
									<Button
										size="sm"
										color="primary"
										onClick={this.handleExportLogsCsv}
										disabled={filteredLogs.length === 0}
									>
										Export CSV
									</Button>
								</div>
								<div className="ml-auto small text-muted">
									Showing {filteredLogs.length} of {logsEntries.length}
								</div>
							</div>
							<div className="table-responsive">
								<table className="table table-sm mb-0">
									<thead>
										<tr>
											<th style={{width: '190px'}}>Time</th>
											<th style={{width: '180px'}}>Actor</th>
											<th>Event</th>
											<th style={{width: '160px'}}>Type</th>
										</tr>
									</thead>
									<tbody>
										{filteredLogs.map((entry) => (
											<tr key={entry.pkey || `${entry.created}-${entry.action}`}>
												<td>{this.formatLogTimestamp(entry.created)}</td>
												<td>{this.getLogActorName(entry) || '-'}</td>
												<td>{this.formatLogDescription(entry)}</td>
												<td>{this.formatStatusLabel(entry.event_type || 'event')}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
							{filteredLogs.length === 0 ? (
								<div className="text-muted mt-3">No logs match the current filters.</div>
							) : null}
						</div>
					) : null}
				</ModalBody>
			</Modal>
		);
	}

	renderJobList() {
		const {jobs, selectedJobId, showCompleted} = this.state;
		const visibleJobs = showCompleted ? jobs : jobs.filter((job) => this.isJobActive(job.status));
		if (visibleJobs.length === 0) {
			return (
				<div className="p-3 text-muted">
					{showCompleted ? 'No jobs found.' : 'No active jobs found.'}
				</div>
			);
		}
		return visibleJobs.map((job) => (
			(() => {
				const timeTotals = this.getJobTimeTotals(job?.workflow?.stations || []);
				const showTimers = timeTotals.totalSeconds > 0 || timeTotals.laborActive || timeTotals.machineActive;
				return (
					<button
						key={job.id}
						type="button"
						onClick={() => this.handleSelectJob(job.id)}
						className="list-group-item list-group-item-action"
						style={job.id === selectedJobId ? trackerStyles.jobListItemActive : trackerStyles.jobListItem}
					>
						<div className="d-flex justify-content-between align-items-center mb-1">
							<strong>{job.name}</strong>
							<Badge color={this.getStatusColor(job.status)} pill>
								{this.formatStatusLabel(job.status)}
							</Badge>
						</div>
						<div className="small" style={job.id === selectedJobId ? {color: '#0f3d67'} : {color: '#6b7280'}}>
							{job.modelName ? `Model: ${job.modelName}` : 'Model not set'}
							{job.quantity !== '' ? ` • Qty: ${job.quantity}` : ''}
						</div>
						{showTimers ? (
							<div style={trackerStyles.jobListTimerRow}>
								{this.renderJobListTimerMetric('Labor', this.formatElapsed(timeTotals.laborSeconds), timeTotals.laborActive, 'primary')}
								{this.renderJobListTimerMetric('Machine', this.formatElapsed(timeTotals.machineSeconds), timeTotals.machineActive, 'info')}
							</div>
						) : null}
					</button>
				);
			})()
		));
	}

	getStationProgress = (station) => {
		const {required, completed} = this.getStationTotals(station);
		const percent = required > 0 ? Math.min(100, Math.round((completed / required) * 100)) : 0;
		return {required, completed, percent};
	}

	renderStationCards(job, stations) {
		if (stations.length === 0) {
			return (
				<div className="text-muted">
					No stations saved for this job.
				</div>
			);
		}
		const jobQuantity = Math.max(1, Number(job.workflow?.job?.quantity ?? job.quantity) || 1);
		const orderedStations = this.getOrderedStations(stations);
		return orderedStations.map((station, index) => {
			const stationKey = this.getStationKey(station);
			const progress = this.getStationProgress(station);
			const hasParts = (station.parts || []).length > 0;
			const noPartsCompletedUnits = this.getNoPartsCompletedUnits(station, jobQuantity);
			const isComplete = hasParts
				? this.isStationComplete(station, jobQuantity)
				: noPartsCompletedUnits >= jobQuantity;
			let availablePartsTotal = progress.required;
			let availableUnits = 0;
			let isLocked = false;
			// if (index > 0) {
			// 	if (hasParts) {
			// 		const availability = this.getAvailableUnitsForStation(orderedStations, index, jobQuantity);
			// 		availableUnits = availability.availableUnits;
			// 		availablePartsTotal = availability.availablePartsTotal;
			// 		const hasUnits = availableUnits >= 1;
			// 		isLocked = !hasUnits;
			// 	} else {
			// 		isLocked = !this.isStationComplete(orderedStations[index - 1], jobQuantity);
			// 	}
			// }
			const stationHasCapacity = index === 0 ? true : !isLocked;
			const requiredPerUnit = progress.required;
			const requiredMax = requiredPerUnit * jobQuantity;
			const completedMax = Math.min(progress.completed, requiredMax);
			let completedForOne = requiredPerUnit > 0 ? (completedMax % requiredPerUnit) : 0;
			if (completedMax > 0 && completedForOne === 0) {
				completedForOne = requiredPerUnit;
			}
			const stationRequiredMax = hasParts ? (progress.required * jobQuantity) : jobQuantity;
			const stationCompletedRaw = hasParts ? progress.completed : noPartsCompletedUnits;
			const stationCompletedCapped = stationRequiredMax > 0
				? Math.min(stationCompletedRaw, stationRequiredMax)
				: 0;
			const stationPercent = stationRequiredMax > 0
				? Math.min(100, Math.round((stationCompletedCapped / stationRequiredMax) * 100))
				: 0;
			return (
				<div key={stationKey} className="card mb-3" style={trackerStyles.stationCard}>
					<div className="card-header d-flex justify-content-between align-items-center" style={trackerStyles.stationCardHeader}>
						<div>
							<strong>{station.station_name || 'Station'}</strong>
							{station.order ? <span className="text-muted ml-2">#{station.order}</span> : null}
						</div>
						<div className="d-flex align-items-center">
							{isLocked && (
								<Badge color="secondary" className="mr-2">
									{hasParts ? 'Waiting on Items' : 'Waiting on Previous'}
								</Badge>
							)}
							<Badge color={this.getStatusColor(station.status || (isComplete ? 'completed' : 'in_progress'))} pill>
								{this.formatStatusLabel(station.status || (isComplete ? 'completed' : 'in_progress'))}
							</Badge>
						</div>
					</div>
					<div className="card-body" style={trackerStyles.stationCardBody}>
						<div className="mb-3">
							<div className="d-flex justify-content-between small text-muted mb-1">
								<span>Progress</span>
								<span>{stationCompletedCapped} / {stationRequiredMax}</span>
							</div>
							<div className="progress" style={{height: '6px'}}>
								<div
									className="progress-bar bg-success"
									role="progressbar"
									style={{width: `${stationPercent}%`}}
									aria-valuenow={stationPercent}
									aria-valuemin="0"
									aria-valuemax="100"
								/>
							</div>
							{/*{index > 0 && hasParts ? (*/}
							{/*	<div className="small text-muted mt-1">*/}
							{/*		Units available from previous: {Math.max(0, Math.round(availableUnits))}*/}
							{/*	</div>*/}
							{/*) : null}*/}
						</div>
							{(station.parts || []).length === 0 ? (
								<div>
									<div className="text-muted">No items defined for this station.</div>
									<div className="small text-muted mt-2">
										Unit progress: {stationCompletedCapped} / {stationRequiredMax}
									</div>
									{this.renderStationTimeSummary(station)}
								</div>
							) : (
								<div className="table-responsive">
									{this.renderStationTimeSummary(station)}
									<table className="table table-bordered mb-0" style={trackerStyles.partsTable}>
									<thead>
										<tr>
											<th className="align-middle">Item</th>
											<th className="align-middle" style={{minWidth: '320px'}}>Labor / Machine Time</th>
											<th className="align-middle" style={{minWidth: '220px'}}>Quantities</th>
										</tr>
									</thead>
										<tbody>
											{station.parts.map((part) => {
												const partKey = this.getPartKey(part);
												const totalSeconds = this.getPartTotalSeconds(part, station);
												const laborSeconds = this.getPartTotalSecondsByType(part, station, 'labor');
												const machineSeconds = this.getPartTotalSecondsByType(part, station, 'machine');
												const laborActive = this.getPartActiveEntryByType(part, station, 'labor');
												const machineActive = this.getPartActiveEntryByType(part, station, 'machine');
											const requiredPerUnit = Number(part.required_quantity) || 0;
											const requiredMax = requiredPerUnit * jobQuantity;
											const rawCompleted = Number(part.completed_quantity) || 0;
											const completedMax = rawCompleted;
											const completedMaxCapped = requiredMax > 0 ? Math.min(rawCompleted, requiredMax) : 0;
											let completedForOne = requiredPerUnit > 0 ? (completedMaxCapped % requiredPerUnit) : 0;
											if (completedMaxCapped > 0 && completedForOne === 0) {
												completedForOne = Math.min(requiredPerUnit, completedMaxCapped);
											}
											const partProgressPercent = requiredMax > 0
												? Math.min(100, Math.round((completedMaxCapped / requiredMax) * 100))
												: 0;
											return (
												<tr key={partKey}>
													<td className="align-middle py-3">
														<div className="font-weight-bold">{part.part_name || 'Item'}</div>
														{part.completed_at ? (
															<div className="small text-muted">Completed</div>
														) : null}
														<div className="progress mt-2" style={{height: '4px'}}>
															<div
																className="progress-bar bg-info"
																role="progressbar"
																style={{width: `${partProgressPercent}%`}}
																aria-valuenow={partProgressPercent}
																aria-valuemin="0"
																aria-valuemax="100"
															/>
														</div>
													</td>
													<td className="align-middle py-3">
														<div className="p-2 rounded" style={{backgroundColor: '#fbfcfd', border: '1px solid #eef1f4'}}>
															{this.renderTimeMetricRow('Labor', this.formatElapsed(laborSeconds), !!laborActive, 'primary')}
															{this.renderTimeMetricRow('Machine', this.formatElapsed(machineSeconds), !!machineActive, 'info')}
															<div className="d-flex align-items-center justify-content-between px-2 pt-1">
																<span className="small text-muted text-uppercase font-weight-bold" style={{letterSpacing: '0.03em'}}>
																	Total
																</span>
																<span style={{fontWeight: 700, fontVariantNumeric: 'tabular-nums'}}>
																	{this.formatElapsed(totalSeconds)}
																</span>
															</div>
														</div>
													</td>
													<td className="align-middle py-3">
														<div>
															{this.renderQuantityBlock('Per Unit', requiredPerUnit, completedForOne)}
															{this.renderQuantityBlock('Total', requiredMax, completedMax)}
														</div>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}
					</div>
				</div>
			);
		});
	}

	renderJobDetails(job) {
		if (!job) {
			return (
				<div className="card" style={trackerStyles.summaryCard}>
					<div className="card-body text-muted">
						Select a job to view its workflow.
					</div>
				</div>
			);
		}
		const workflow = job.workflow;
		if (!workflow) {
			return (
				<div className="card" style={trackerStyles.summaryCard}>
					<div className="card-body">
						<div className="text-muted">Workflow data is missing for this job.</div>
					</div>
				</div>
			);
		}
		const stations = workflow.stations || [];
		const stationCount = stations.length;
		const partCount = stations.reduce((sum, station) => sum + ((station.parts || []).length || 0), 0);
		const totalQuantityPerUnit = stations.reduce(
			(sum, station) => sum + (station.parts || []).reduce((pSum, part) => pSum + (Number(part.required_quantity) || 0), 0),
			0
		);
		const jobQuantity = Math.max(1, Number(workflow.job?.quantity ?? job.quantity) || 1);
		const workflowSummary = this.getWorkflowSummary(stations, jobQuantity);
		const timeTotals = this.getJobTimeTotals(stations);
		const completedQuantity = workflowSummary.completedQuantity;
		const plannedQuantity = workflowSummary.requiredQuantity;
		const stationProgressDetail = `${workflowSummary.completedStations} completed • ${workflowSummary.inProgressStations} in progress • ${workflowSummary.pendingStations} pending`;
		return (
			<div>
				<div className="card mb-3" style={trackerStyles.summaryCard}>
					<div className="card-header d-flex justify-content-between align-items-center" style={trackerStyles.summaryCardHeader}>
						<div>
							<strong>{job.name}</strong>
							{job.modelName ? <span className="text-muted ml-2">{job.modelName}</span> : null}
						</div>
						<div className="d-flex align-items-center">
							<Button
								size="sm"
								outline
								color="secondary"
								className="mr-2"
								onClick={() => this.handleOpenLogs(job)}
							>
								View Logs
							</Button>
							<Badge color={this.getStatusColor(job.status)} pill>
								{this.formatStatusLabel(job.status)}
							</Badge>
						</div>
					</div>
					<div className="card-body">
						<div className="row">
							<div className="col-lg-8">
								{this.renderSummaryProgress(
									'Workflow Stations',
									workflowSummary.completedStations,
									workflowSummary.totalStations,
									workflowSummary.workflowPercent,
									stationProgressDetail,
									'bg-success'
								)}
								{this.renderSummaryProgress(
									'Workflow Quantity',
									completedQuantity,
									plannedQuantity,
									workflowSummary.quantityPercent,
									`${partCount} item lines tracked across stations`,
									'bg-info'
								)}
								{this.renderSummaryProgress(
									'Unit Output',
									workflowSummary.unitsCompleted,
									jobQuantity,
									workflowSummary.unitsPercent,
									'Completed units at final workflow station',
									'bg-primary'
								)}
								<div className="row mt-2">
									<div className="col-sm-6 col-md-3 mb-2">
										<div style={trackerStyles.summaryStatTile}>
											<div style={trackerStyles.summaryStatLabel}>Quantity</div>
											<div style={trackerStyles.summaryStatValue}>{jobQuantity}</div>
										</div>
									</div>
									<div className="col-sm-6 col-md-3 mb-2">
										<div style={trackerStyles.summaryStatTile}>
											<div style={trackerStyles.summaryStatLabel}>Stations Used</div>
											<div style={trackerStyles.summaryStatValue}>{stationCount}</div>
										</div>
									</div>
									<div className="col-sm-6 col-md-3 mb-2">
										<div style={trackerStyles.summaryStatTile}>
											<div style={trackerStyles.summaryStatLabel}>Item Lines</div>
											<div style={trackerStyles.summaryStatValue}>{partCount}</div>
										</div>
									</div>
									<div className="col-sm-6 col-md-3 mb-2">
										<div style={trackerStyles.summaryStatTile}>
											<div style={trackerStyles.summaryStatLabel}>Qty / Model</div>
											<div style={trackerStyles.summaryStatValue}>{totalQuantityPerUnit}</div>
										</div>
									</div>
								</div>
								<div className="small text-muted mt-2">
									Workflow Quantity totals all required item quantities across stations. Unit Output follows completed units at the final workflow station, and Qty / Model is the planned item quantity per finished unit.
								</div>
							</div>
							<div className="col-lg-4 mt-3 mt-lg-0">
								<div style={trackerStyles.summaryTimeCard}>
									<div style={trackerStyles.summaryTimeTitle}>Total Time</div>
									{this.renderTimeMetricRow('Labor', this.formatElapsed(timeTotals.laborSeconds), timeTotals.laborActive, 'primary')}
									{this.renderTimeMetricRow('Machine', this.formatElapsed(timeTotals.machineSeconds), timeTotals.machineActive, 'info')}
									<div className="d-flex align-items-center justify-content-between px-2 pt-1">
										<span className="small text-muted text-uppercase font-weight-bold" style={{letterSpacing: '0.03em'}}>
											Total
										</span>
										<span style={{fontWeight: 700, fontVariantNumeric: 'tabular-nums'}}>
											{this.formatElapsed(timeTotals.totalSeconds)}
										</span>
									</div>
									<div className="small text-muted mt-2">
										Active entries: {timeTotals.activeEntryCount}
									</div>
								</div>
							</div>
						</div>
						{job.lotId ? (
							<div className="text-muted small mt-2">
								Lot: {job.lotId}
							</div>
						) : null}
					</div>
				</div>
				{this.renderStationCards(job, stations)}
			</div>
		);
	}

	render() {
		if (this.state.loading) {
			return <Framework.Elements.LoadingSpinner isLoading={true}/>;
		}
		const visibleJobs = this.state.showCompleted
			? this.state.jobs
			: this.state.jobs.filter((job) => this.isJobActive(job.status));
		const activeJobsCount = this.state.jobs.filter((job) => this.isJobActive(job.status)).length;
		const selectedJob = this.state.jobs.find((job) => job.id === this.state.selectedJobId);
		const selectedJobStations = selectedJob?.workflow?.stations || [];
		const selectedJobStationCount = selectedJobStations.length;
		const selectedJobPartCount = selectedJobStations.reduce((sum, station) => sum + ((station.parts || []).length || 0), 0);
		return (
			<div className="row">
				<div className="col-12 mb-3">
					<div style={trackerStyles.heroBand}>
						<div className="row align-items-center">
							<div className="col-lg-8">
								<div style={trackerStyles.heroEyebrow}>ERP Tracker</div>
								<h2 style={trackerStyles.heroTitle}>Track Production Progress Across Each Station</h2>
								<div style={trackerStyles.heroSubtext}>
									Use ERP tracking to review workflow status, unit completion, labor and machine time, and item-level progress for active jobs.
								</div>
							</div>
							<div className="col-lg-4 mt-3 mt-lg-0">
								<div style={trackerStyles.heroStatsWrap}>
									<div style={trackerStyles.heroStatRow}><span style={trackerStyles.heroStatLabel}>Visible Jobs</span><strong style={trackerStyles.heroStatValue}>{visibleJobs.length}</strong></div>
									<div style={trackerStyles.heroStatRow}><span style={trackerStyles.heroStatLabel}>Active Jobs</span><strong style={trackerStyles.heroStatValue}>{activeJobsCount}</strong></div>
									<div style={trackerStyles.heroStatRow}><span style={trackerStyles.heroStatLabel}>Current Workflow</span><strong style={trackerStyles.heroStatValue}>{selectedJobStationCount} st / {selectedJobPartCount} items</strong></div>
									<div className="mt-2">
										<MrpLocationControl
											rootState={this.props.rootState}
											id="mrp-job-tracker-location"
											wrapperStyle={{marginTop: '12px'}}
											selectStyle={trackerStyles.heroSelect}
											note={LOCATION_NOTE}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="col-lg-3 col-md-4 mb-3">
					<div className="card h-100" style={trackerStyles.jobsRailCard}>
						<div className="card-header d-flex justify-content-between align-items-center" style={trackerStyles.jobsRailHeader}>
							<strong>Jobs</strong>
							<div className="d-flex align-items-center">
								<Button
									size="sm"
									outline
									color={this.state.showCompleted ? 'secondary' : 'primary'}
									style={trackerStyles.filterButton}
									onClick={this.handleToggleShowCompleted}
								>
									{this.state.showCompleted ? 'Hide Completed' : 'Show Completed'}
								</Button>
								<Button size="sm" outline color="primary" style={trackerStyles.refreshButton} onClick={this.loadJobs} className="ml-2">
									<i className="fa fa-refresh"/>
								</Button>
							</div>
						</div>
						<div className="list-group list-group-flush" style={trackerStyles.jobsRailBody}>
							{this.state.loadError ? (
								<div className="p-3 text-danger">{this.state.loadError}</div>
							) : this.renderJobList()}
						</div>
					</div>
				</div>
				<div className="col-lg-9 col-md-8">
					{this.renderJobDetails(selectedJob)}
				</div>
				{this.renderLogsModal()}
			</div>
		);
	}
}

const trackerStyles = {
	heroBand: {
		background: 'linear-gradient(135deg, #eef6ff 0%, #f9fcff 42%, #e5f4f8 100%)',
		border: '1px solid #d6e2f0',
		borderTop: '4px solid #2d79b5',
		borderRadius: '14px',
		padding: '22px 24px',
		color: '#17324d',
		boxShadow: '0 10px 24px rgba(12, 55, 92, 0.08)',
	},
	heroEyebrow: {
		display: 'inline-flex',
		alignItems: 'center',
		padding: '4px 10px',
		borderRadius: '999px',
		backgroundColor: '#dceeff',
		color: '#1f689d',
		fontSize: '11px',
		textTransform: 'uppercase',
		letterSpacing: '0.08em',
		fontWeight: 700,
	},
	heroTitle: {
		fontSize: '27px',
		lineHeight: 1.2,
		margin: '8px 0 10px',
		fontWeight: 700,
	},
	heroSubtext: {
		fontSize: '14px',
		color: '#557086',
		maxWidth: '560px',
	},
	heroStatsWrap: {
		background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, #edf7fb 100%)',
		border: '1px solid #d7e4f2',
		borderRadius: '12px',
		padding: '12px 14px',
		boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.78)',
	},
	heroSelect: {
		backgroundColor: '#ffffff',
		border: '1px solid #c9d9e8',
		color: '#17324d',
	},
	heroStatRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		fontSize: '13px',
		padding: '7px 0',
		borderBottom: '1px solid #e3ecf5',
		gap: '8px',
	},
	heroStatLabel: {
		color: '#5f7489',
	},
	heroStatValue: {
		color: '#17324d',
		fontSize: '15px',
		fontWeight: 700,
	},
	jobsRailCard: {
		borderRadius: '12px',
		border: '1px solid #d6e2f0',
		boxShadow: '0 8px 18px rgba(12, 55, 92, 0.09)',
		overflow: 'hidden',
	},
	jobsRailHeader: {
		background: 'linear-gradient(90deg, #f8fbff 0%, #edf4ff 100%)',
		borderBottom: '1px solid #d7e4f4',
	},
	jobsRailBody: {
		backgroundColor: '#f9fcff',
	},
	filterButton: {
		borderColor: '#9fb7d4',
		color: '#2c5278',
		backgroundColor: '#f6faff',
	},
	refreshButton: {
		boxShadow: '0 6px 14px rgba(16, 93, 148, 0.18)',
	},
	jobListItem: {
		backgroundColor: '#fff',
		borderLeft: '4px solid transparent',
	},
	jobListItemActive: {
		backgroundColor: '#e9f4ff',
		borderLeft: '4px solid #1e6db0',
		color: '#123b63',
	},
	jobListTimerRow: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: '6px',
		marginTop: '8px',
	},
	jobListTimerChip: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: '5px',
		padding: '4px 8px',
		borderRadius: '999px',
		fontSize: '11px',
		fontWeight: 600,
	},
	jobListTimerLabel: {
		textTransform: 'uppercase',
		letterSpacing: '0.03em',
	},
	jobListTimerValue: {
		fontVariantNumeric: 'tabular-nums',
	},
	jobListTimerRunning: {
		padding: '1px 6px',
		borderRadius: '999px',
		backgroundColor: 'rgba(17, 24, 39, 0.1)',
		fontSize: '10px',
		fontWeight: 700,
		letterSpacing: '0.03em',
		textTransform: 'uppercase',
	},
	summaryCard: {
		borderRadius: '12px',
		border: '1px solid #d6e2f0',
		boxShadow: '0 8px 18px rgba(12, 55, 92, 0.09)',
		overflow: 'hidden',
	},
	summaryCardHeader: {
		background: 'linear-gradient(90deg, #f8fbff 0%, #edf4ff 100%)',
		borderBottom: '1px solid #d7e4f4',
	},
	summaryProgressBlock: {
		backgroundColor: '#f8fbff',
		border: '1px solid #e2ebf7',
	},
	summaryStatTile: {
		backgroundColor: '#f7fafc',
		border: '1px solid #e3e9f2',
		borderRadius: '8px',
		padding: '10px 12px',
		height: '100%',
	},
	summaryStatLabel: {
		fontSize: '11px',
		textTransform: 'uppercase',
		letterSpacing: '0.05em',
		color: '#6b7280',
		fontWeight: 700,
	},
	summaryStatValue: {
		fontSize: '21px',
		lineHeight: 1.2,
		fontWeight: 700,
		color: '#1f2933',
		fontVariantNumeric: 'tabular-nums',
	},
	summaryTimeCard: {
		backgroundColor: '#fbfcfd',
		border: '1px solid #e5ebf2',
		borderRadius: '10px',
		padding: '10px',
		height: '100%',
	},
	summaryTimeTitle: {
		fontSize: '12px',
		textTransform: 'uppercase',
		letterSpacing: '0.06em',
		fontWeight: 700,
		color: '#4b5563',
		marginBottom: '8px',
	},
	stationTimeSummaryCard: {
		backgroundColor: '#fbfcfd',
		border: '1px solid #e5ebf2',
		marginBottom: '12px',
	},
	stationTimeSummaryTitle: {
		fontSize: '11px',
		textTransform: 'uppercase',
		letterSpacing: '0.05em',
		fontWeight: 700,
		color: '#6b7280',
		padding: '0 8px 6px',
	},
	stationCard: {
		borderRadius: '12px',
		border: '1px solid #d6e2f0',
		boxShadow: '0 8px 18px rgba(12, 55, 92, 0.09)',
		overflow: 'hidden',
	},
	stationCardHeader: {
		background: 'linear-gradient(90deg, #f8fbff 0%, #edf4ff 100%)',
		borderBottom: '1px solid #d7e4f4',
	},
	stationCardBody: {
		backgroundColor: '#fcfeff',
	},
	partsTable: {
		backgroundColor: '#fff',
	},
};

export default Framework.Components.withRootState(MrpJobTracker);
